import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, ThumbsUp, ThumbsDown, NotePencil } from '@phosphor-icons/react';
import { jstDateTime } from '@/utils/jstDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [[7, '7日間'], [30, '30日間'], [90, '90日間']];
const RATINGS = [['all', 'すべて'], ['down', '👎 低評価'], ['up', '👍 高評価']];

export default function EngAiFeedback() {
  const [days, setDays] = useState(30);
  const [rating, setRating] = useState('down');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [drafting, setDrafting] = useState(null);
  const navigate = useNavigate();

  const createDraft = async (r) => {
    if (drafting) return;
    setDrafting(r.message_id || r.question);
    try {
      const title = (r.question || 'ナレッジ追加').slice(0, 60);
      const html = `<p><strong>この記事は、AIアシスタントが回答できなかった質問から作成された下書きです。</strong></p>`
        + `<p>質問: ${r.question || ''}</p>`
        + `<p>AIの回答（不十分だった内容）: ${r.answer_preview || ''}</p>`
        + `<hr/><p>ここに正しい情報・手順・参考資料を記載してください。公開するとAIが次回からこの記事を根拠に回答できます。</p>`;
      const res = await axios.post(`${API}/knowledge/articles`, { title, content_html: html, status: 'draft' });
      toast.success('ナレッジ下書きを作成しました');
      navigate(`/knowledge/p/${res.data.id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '下書きの作成に失敗しました');
    } finally { setDrafting(null); }
  };

  useEffect(() => {
    axios.get(`${API}/assistant/feedback`, { params: { days, rating } })
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e?.response?.data?.detail || '読み込みに失敗しました'));
  }, [days, rating]);

  const total = (data?.up || 0) + (data?.down || 0);
  return (
    <div className="space-y-4" data-testid="eng-ai-feedback-page">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 flex items-center gap-2">
          <Sparkle size={22} weight="duotone" className="text-violet-600" /> AIフィードバック
        </h1>
        <p className="w-full sm:w-auto sm:ml-2 text-[12px] text-zinc-400">チャットAIアシスタントの回答への評価 — 低評価はナレッジ改善のヒントです</p>
      </div>

      {error ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800" data-testid="eng-ai-feedback-error">{error}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700" data-testid="eng-ai-fb-up">
              <ThumbsUp size={13} weight="fill" /> {data?.up ?? '—'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[12px] font-semibold text-rose-700" data-testid="eng-ai-fb-down">
              <ThumbsDown size={13} weight="fill" /> {data?.down ?? '—'}
            </span>
            {total > 0 && (
              <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-[12px] font-semibold text-zinc-600" data-testid="eng-ai-fb-rate">
                満足度 {Math.round(((data?.up || 0) / total) * 100)}%
              </span>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <div className="flex rounded-lg border border-zinc-200 overflow-hidden">
                {RATINGS.map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setRating(v)}
                    data-testid={`eng-ai-fb-rating-${v}`}
                    className={`px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${rating === v ? 'bg-violet-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex rounded-lg border border-zinc-200 overflow-hidden">
                {PERIODS.map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setDays(v)}
                    data-testid={`eng-ai-fb-days-${v}`}
                    className={`px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${days === v ? 'bg-teal-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2" data-testid="eng-ai-fb-list">
            {(data?.items || []).length === 0 && (
              <p className="rounded-xl border border-dashed border-zinc-300 bg-white px-5 py-12 text-center text-[13px] text-zinc-400" data-testid="eng-ai-fb-empty">
                該当するフィードバックがありません — チャットのAIアシスタントの回答に 👍/👎 が付くとここに表示されます
              </p>
            )}
            {(data?.items || []).map((r, i) => (
              <div key={i} data-testid={`eng-ai-fb-item-${i}`}
                className={`rounded-xl border bg-white px-4 py-3 shadow-sm ${r.rating === 'down' ? 'border-rose-100' : 'border-emerald-100'}`}>
                <div className="flex items-start gap-2.5">
                  <span className={`mt-0.5 shrink-0 rounded-full p-1.5 ${r.rating === 'down' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    {r.rating === 'down' ? <ThumbsDown size={14} weight="fill" /> : <ThumbsUp size={14} weight="fill" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-zinc-800">Q: {r.question || '（質問不明）'}</p>
                    <p className="mt-0.5 line-clamp-2 text-[12px] text-zinc-500">A: {r.answer_preview}</p>
                    <p className="mt-1 text-[10.5px] text-zinc-400">
                      {jstDateTime(r.ts)} · {r.user_name}{r.tool ? ` · ツール: ${r.tool}` : ''}
                    </p>
                  </div>
                  {r.rating === 'down' && (
                    <button type="button" onClick={() => createDraft(r)} disabled={!!drafting}
                      data-testid={`eng-ai-fb-draft-${i}`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-[11px] font-semibold text-violet-700 transition-colors hover:bg-violet-100 disabled:opacity-50">
                      <NotePencil size={13} /> ナレッジ下書きを作成
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
